/**
 * The continuous sounds (04 §5), from state rather than events: the tractor beam's warble and
 * the lance's burn last as long as their flag is set. Each is a few drones (sfx/ambient.ts)
 * built here as WebAudio nodes; they are not voices of the cap, which is for things that end.
 */

import type { Bus } from './bus';
import { DRONES, type DroneSpec } from './sfx';
import type { Synth } from './synth';
import type { Ambience } from './types';

/** The fade when a drone is let go, as a time constant: silent in a tenth of a second, no click. */
const RELEASE = 0.025;
const KEYS: readonly (keyof Ambience)[] = ['beam', 'lance'];

/** A drone once started: its level, and everything to stop. */
interface Drone {
  readonly amp: GainNode;
  readonly sources: AudioScheduledSourceNode[];
}

export class AmbienceSound {
  private readonly on = new Map<keyof Ambience, Drone[]>();

  constructor(
    private readonly bus: Bus,
    private readonly synth: Synth,
  ) {}

  /** Safe to call every tick: only a flag that has changed does anything. */
  set(a: Ambience): void {
    for (const key of KEYS) {
      const playing = this.on.get(key);
      if (a[key] && !playing && this.bus.live) {
        const now = this.bus.now;
        this.on.set(key, DRONES[key].map((d) => this.start(d, now)));
      }
      if (!a[key] && playing) {
        this.on.delete(key);
        for (const d of playing) this.stop(d);
      }
    }
  }

  private start(spec: DroneSpec, now: number): Drone {
    const ctx = this.bus.ctx;
    const nodes: AudioNode[] = [];
    const sources: AudioScheduledSourceNode[] = [];
    const src = this.source(spec);
    nodes.push(src);
    sources.push(src);
    // Older Safari has no detune on a buffer source: the noise then burns steadily, which is no loss.
    const detune: AudioParam | undefined = src.detune;
    // The two tones: a square LFO throws the pitch between `freq` and `cents` above it.
    if (spec.warble && detune) {
      detune.value = spec.warble.cents / 2;
      sources.push(this.lfo('square', spec.warble.rate, spec.warble.cents / 2, detune, nodes));
    }
    if (spec.drift && detune) sources.push(this.lfo('sine', spec.drift.rate, spec.drift.cents, detune, nodes));
    let head: AudioNode = src;
    if (spec.filter) {
      const bq = ctx.createBiquadFilter();
      bq.type = spec.filter.type;
      bq.frequency.value = spec.filter.freq;
      bq.Q.value = spec.filter.q;
      nodes.push(bq);
      head = head.connect(bq);
    }
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0, now);
    amp.gain.setTargetAtTime(spec.level, now, spec.attack / 3);
    nodes.push(amp);
    head.connect(amp).connect(this.bus.sfx);
    for (const s of sources) s.start(now);
    src.onended = () => { for (const n of nodes) n.disconnect(); };
    return { amp, sources };
  }

  private stop(d: Drone): void {
    const now = this.bus.now;
    d.amp.gain.cancelScheduledValues(now);
    d.amp.gain.setTargetAtTime(0, now, RELEASE);
    for (const s of d.sources) s.stop(now + RELEASE * 8);
  }

  private source(spec: DroneSpec): OscillatorNode | AudioBufferSourceNode {
    const ctx = this.bus.ctx;
    if (spec.wave === 'noise') {
      const b = ctx.createBufferSource();
      b.buffer = this.bus.noise;
      b.loop = true;
      b.playbackRate.value = spec.freq / this.bus.noiseHz;
      return b;
    }
    const osc = ctx.createOscillator();
    if (spec.wave === 'pulse') osc.setPeriodicWave(this.synth.pulseWave(spec.width ?? 0.5));
    else osc.type = spec.wave;
    osc.frequency.value = spec.freq;
    return osc;
  }

  /** An LFO of `depth` cents into `target`; the caller starts it with the rest. */
  private lfo(
    type: OscillatorType, rate: number, depth: number, target: AudioParam, nodes: AudioNode[],
  ): OscillatorNode {
    const osc = this.bus.ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = rate;
    const gain = this.bus.ctx.createGain();
    gain.gain.value = depth;
    osc.connect(gain).connect(target);
    nodes.push(osc, gain);
    return osc;
  }
}
